/**
 * `/progress` — the learner analytics deep-dive (SCH-AI-2b,
 * SCHOOLOGY_STUDENT_AI_PLAN Slice 2b).
 *
 * Four cards over four queries, all zero-LLM server math this screen only
 * formats: **Exam readiness** (`analytics.trend` → RadialGauge of the
 * projected exam-day score vs target with the 80% CI band — hidden until
 * the projection is real; /home owns the unlock ladder), **Skill mastery**
 * (`analytics.mastery` → weakest-first BarList with tenant-config band
 * labels + a practice CTA), **Pacing** (`analytics.pacing` → avg vs budget,
 * rushed/stalled, slowest list — OWN data only), and **Questions to
 * revisit** (`analytics.weakAreas` → missed by you, easiest-for-the-cohort
 * first; cohort numbers arrive ONLY as the server's k-floored aggregate).
 *
 * Gated by `learnerAnalyticsEnabled` exactly like /home: FORBIDDEN from any
 * proc ⇒ redirect to `/catalog` instead of erroring. Mobile-first: single
 * column at 390px (`grid-cols-1` = minmax(0,1fr) — an implicit auto track
 * would size to the widest card's max-content and overflow narrow phones),
 * two columns from `lg` with the mastery card spanning both.
 */
import * as React from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { BarChart3, RotateCcw, Target, Timer, TrendingUp } from 'lucide-react';
import { KpiCard } from '@mangrove/ui';
import { trpc } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';
import { CourseFilter } from '../components/course-filter.js';
import { MasteryCard } from '../components/progress/mastery-card.js';
import { PacingCard } from '../components/progress/pacing-card.js';
import { ReadinessCard } from '../components/progress/readiness-card.js';
import { WeakAreasCard } from '../components/progress/weak-areas-card.js';

export const Route = createFileRoute('/progress')({
  component: ProgressPage,
});

function ProgressPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Progress" wide>
        <ProgressContent />
      </LearnShell>
    </RequireAuth>
  );
}

function ProgressContent(): React.JSX.Element {
  const [courseId, setCourseId] = React.useState('');
  const scope = courseId ? { courseId } : undefined;
  const trendQ = trpc.learning.learner.analytics.trend.useQuery(scope);
  const masteryQ = trpc.learning.learner.analytics.mastery.useQuery(scope);
  const pacingQ = trpc.learning.learner.analytics.pacing.useQuery(scope);
  const weakAreasQ = trpc.learning.learner.analytics.weakAreas.useQuery(scope);

  // Flag off (learnerAnalyticsEnabled=false) ⇒ FORBIDDEN from any proc —
  // fall back to the catalog instead of rendering an error screen.
  if (
    trendQ.error?.data?.code === 'FORBIDDEN' ||
    masteryQ.error?.data?.code === 'FORBIDDEN' ||
    pacingQ.error?.data?.code === 'FORBIDDEN' ||
    weakAreasQ.error?.data?.code === 'FORBIDDEN'
  ) {
    return <Navigate to="/catalog" replace />;
  }

  // Honest KPI strip derived from the same four queries — no separate fetch,
  // no fabricated metrics. Overall mastery + skills tracked come from the
  // mastery list, average pace from pacing, the review count from weakAreas.
  const masteryRows = masteryQ.data;
  const overallMastery =
    masteryRows && masteryRows.length > 0
      ? Math.round((masteryRows.reduce((sum, r) => sum + r.mastery, 0) / masteryRows.length) * 100)
      : null;
  const skillsTracked = masteryRows?.length ?? null;
  const avgPace = pacingQ.data?.avgSecondsPerQuestion ?? null;
  const budgetPace = pacingQ.data?.budgetSecondsPerQuestion ?? null;
  const toRevisit = weakAreasQ.data?.length ?? null;

  const masteryAccent =
    overallMastery == null
      ? 'primary'
      : overallMastery >= 80
        ? 'success'
        : overallMastery >= 50
          ? 'warning'
          : 'destructive';
  const paceAccent =
    avgPace != null && budgetPace != null && avgPace > budgetPace ? 'warning' : 'primary';

  return (
    <div className="space-y-6">
      <div className="animate-fade-in flex flex-col gap-3 rounded-xl border border-l-2 border-l-primary/40 bg-gradient-to-br from-grad-green to-grad-blue px-4 py-2 sm:flex-row sm:items-start sm:justify-between dark:from-grad-green/15 dark:to-grad-blue/15">
        <div className="space-y-1">
          <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
            <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary">
              <TrendingUp className="size-4" aria-hidden="true" />
            </span>
            Your progress
          </h2>
          <p className="text-sm text-muted-foreground">
            Where you're strong, where you're slow, and what to revisit.
          </p>
        </div>
        <CourseFilter value={courseId} onChange={setCourseId} className="sm:mt-0.5" />
      </div>

      {/* KPI strip — at-a-glance metrics, all derived from the real queries. */}
      <div className="ls-stagger grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={BarChart3}
          accent={masteryAccent}
          label="Overall mastery"
          value={overallMastery == null ? '—' : `${overallMastery}%`}
          hint={
            skillsTracked == null
              ? undefined
              : `across ${skillsTracked} skill area${skillsTracked === 1 ? '' : 's'}`
          }
          isLoading={masteryQ.isPending}
        />
        <KpiCard
          icon={Target}
          accent="primary"
          label="Skills tracked"
          value={skillsTracked == null ? '—' : skillsTracked}
          hint="from your quiz history"
          isLoading={masteryQ.isPending}
        />
        <KpiCard
          icon={Timer}
          accent={paceAccent}
          label="Avg per question"
          value={avgPace == null ? '—' : `${Math.round(avgPace)}s`}
          hint={budgetPace == null ? 'no timed tests yet' : `budget ${Math.round(budgetPace)}s`}
          isLoading={pacingQ.isPending}
        />
        <KpiCard
          icon={RotateCcw}
          accent={toRevisit != null && toRevisit > 0 ? 'warning' : 'primary'}
          label="To revisit"
          value={toRevisit == null ? '—' : toRevisit}
          hint="questions in your queue"
          isLoading={weakAreasQ.isPending}
        />
      </div>

      {/* Hidden until the projection is real — /home owns the unlock ladder. */}
      <ReadinessCard trend={trendQ.data} />

      {/* Showpiece: the full-width mastery map (radar + weakest-first readout). */}
      <div className="min-w-0">
        <MasteryCard
          rows={masteryQ.data}
          isPending={masteryQ.isPending}
          isError={masteryQ.isError}
          onRetry={() => void masteryQ.refetch()}
        />
      </div>

      {/* Bottom row: pacing (wider) paired with the questions-to-revisit queue. */}
      <div className="ls-stagger grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="min-w-0">
          <PacingCard
            pacing={pacingQ.data}
            isPending={pacingQ.isPending}
            isError={pacingQ.isError}
            onRetry={() => void pacingQ.refetch()}
          />
        </div>
        <div className="min-w-0">
          <WeakAreasCard
            rows={weakAreasQ.data}
            isPending={weakAreasQ.isPending}
            isError={weakAreasQ.isError}
            onRetry={() => void weakAreasQ.refetch()}
          />
        </div>
      </div>
    </div>
  );
}
